import { roundNumber, RoundNumberFactor } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';

// Loose shape on purpose: accepts a hydrated `Boat` model as well as the raw API
// rows and payload drafts that only carry the dimensions.
export interface BoatAreaSource {
    length?: number | null;
    width?: number | null;
    sqm?: number | null;
}

/**
 * Single source of truth for a boat's area in m² — the frontend mirror of the backend's
 * `resolveBoatSqm()` (`app/bcm/@helpers/resolve-boat-sqm.ts`).
 *
 * The persisted `sqm` wins; `length × width` only fills in for boats without one, matching the
 * `COALESCE(sqm, ROUND(length * width, 2))` in `bcm_evaluate_dynamic_price`. Deliberately NOT
 * `resolveBoatLength()`: both the stored area and the SQL engine calculate with the hull length,
 * so using the LüA here would offer a quantity the invoice then bills differently.
 */
export function resolveBoatArea(boat: BoatAreaSource | null | undefined): number | null {

    if (!boat) {
        return null;
    }

    const sqm = Number(boat.sqm);

    if (Number.isFinite(sqm) && sqm > 0) {
        return roundNumber(sqm, RoundNumberFactor.TwoDecimals);
    }

    const length = Number(boat.length);
    const width = Number(boat.width);

    if (Number.isFinite(length) && length > 0 && Number.isFinite(width) && width > 0) {
        return roundNumber(length * width, RoundNumberFactor.TwoDecimals);
    }

    return null;
}
